/**
 * Sezioni presentazionali del CV parsato in struttura Europass.
 *
 * Espone come componenti indipendenti le sezioni del "mega oggetto JSON"
 * ({@link EuropassCv}) estratto dal Cv_Pdf di un candidato esterno (esperienza
 * lavorativa, istruzione, lingue, competenze, progetti, riconoscimenti,
 * pubblicazioni) più le card dei dati personali ({@link EuropassPersonalInfoCard})
 * e del profilo professionale ({@link EuropassSummaryCard}). Non esiste più un
 * contenitore "viewer": le sezioni sono renderizzate dalla pagina di dettaglio
 * nello stesso flusso unico di tutte le altre sezioni del profilo.
 *
 * Sono puramente presentazionali e difensive: ogni sezione e ogni campo del CV
 * è opzionale (il parser omette ciò che non trova), quindi le sezioni vuote
 * ritornano `null` e i singoli valori assenti sono semplicemente saltati.
 *
 * Stile: classi BEM `europass-cv__*` in `styles.css`, con riuso di `Card` e
 * `Badge` del design system. Le stringhe visibili all'utente sono in italiano;
 * i valori (date, testi) sono mostrati così come estratti dal CV.
 */
import { useState, type ReactNode } from 'react';
import type {
  EuropassAward,
  EuropassCv,
  EuropassEducation,
  EuropassLanguageSkill,
  EuropassProject,
  EuropassPublication,
  EuropassWorkExperience,
} from '../shared/dto';
import {
  ItemEditControls,
  SectionEditButton,
  SectionItemModal,
  SingleSectionModal,
} from './EuropassCvSectionEditor';
import type {
  RepeatableSection,
  SectionItemByName,
  SingleSection,
} from './europassCvSectionSchemas';
import { Badge, Card } from './ui';

/**
 * Prop opzionali dell'Editable_Mode condivise da tutte le sezioni ripetibili
 * del CV. Quando `editable` è `false`/assente le sezioni NON renderizzano alcun
 * controllo di modifica e non invocano alcun callback: il comportamento è
 * identico alla vista read-only del flusso CV esterni.
 */
export interface EuropassEditableSectionProps {
  /** Abilita i controlli di modifica/aggiunta/rimozione. Default `false`. */
  editable?: boolean;
  /** Aggiunge un item alla sezione ripetibile indicata. */
  onItemAdd?: (section: string, item: unknown) => Promise<void>;
  /** Aggiorna l'item alla posizione `index` della sezione (via `onSectionUpdate`). */
  onSectionUpdate?: (section: keyof EuropassCv, data: unknown) => Promise<void>;
  /** Rimuove l'item alla posizione `index` della sezione ripetibile. */
  onItemRemove?: (section: string, index: number) => Promise<void>;
}

/**
 * Hook interno che incapsula lo stato e i gestori dell'Editable_Mode di una
 * sezione ripetibile: apertura del form di aggiunta/modifica, salvataggio e
 * rimozione con tracciamento del "busy" per riga. Riusato da ogni sezione
 * ripetibile per evitare duplicazione.
 *
 * Il salvataggio di una modifica di un item esistente ricompone l'intero array
 * della sezione (item aggiornato all'indice) e lo persiste via `onSectionUpdate`
 * (contratto `PUT /sections`), coerente con il design: non c'è un endpoint di
 * update puntuale, l'aggiornamento di una sezione sostituisce la sezione.
 */
function useSectionEditing<S extends RepeatableSection>(
  section: S,
  entries: SectionItemByName[S][],
  edit: EuropassEditableSectionProps,
) {
  const [modal, setModal] = useState<
    { mode: 'add' } | { mode: 'edit'; index: number } | null
  >(null);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);

  const active = edit.editable === true;

  const openAdd = () => setModal({ mode: 'add' });
  const openEdit = (index: number) => setModal({ mode: 'edit', index });
  const close = () => setModal(null);

  const submit = async (item: SectionItemByName[S]): Promise<void> => {
    if (modal === null) return;
    if (modal.mode === 'add') {
      await edit.onItemAdd?.(section, item);
      return;
    }
    // Modifica: ricompone l'array con l'item aggiornato all'indice e persiste
    // l'intera sezione (nessun endpoint di update puntuale, vedi design).
    const next = entries.map((e, i) => (i === modal.index ? item : e));
    await edit.onSectionUpdate?.(section as keyof EuropassCv, next);
  };

  const remove = async (index: number): Promise<void> => {
    setBusyIndex(index);
    try {
      await edit.onItemRemove?.(section, index);
    } finally {
      setBusyIndex(null);
    }
  };

  return { active, modal, busyIndex, openAdd, openEdit, close, submit, remove };
}

/**
 * Hook interno per l'Editable_Mode di una SEZIONE SINGOLA (dati personali,
 * profilo, competenze): gestisce l'apertura del modal di modifica e il
 * salvataggio via `onSectionUpdate`. Riusato dalle tre card singole.
 */
function useSingleSectionEditing(
  section: SingleSection,
  edit: EuropassEditableSectionProps,
) {
  const [open, setOpen] = useState(false);
  const active = edit.editable === true;
  const openModal = () => setOpen(true);
  const close = () => setOpen(false);
  const submit = async (data: unknown): Promise<void> => {
    await edit.onSectionUpdate?.(section as keyof EuropassCv, data);
  };
  return { active, open, openModal, close, submit };
}

/** Restituisce `true` se la stringa è presente e non vuota dopo trim. */
function has(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * Compone un intervallo temporale leggibile da `startDate`/`endDate`/`current`.
 * Le date sono testo libero (come nel CV): non vengono normalizzate. Restituisce
 * `undefined` se non c'è alcun segnale temporale.
 */
function formatPeriod(
  startDate: string | undefined,
  endDate: string | undefined,
  current: boolean | undefined,
): string | undefined {
  const start = has(startDate) ? startDate.trim() : undefined;
  const end = current === true ? 'Presente' : has(endDate) ? endDate.trim() : undefined;
  if (start !== undefined && end !== undefined) return `${start} – ${end}`;
  if (start !== undefined) return start;
  if (end !== undefined) return end;
  return undefined;
}

/** Unisce più frammenti non vuoti con un separatore (per città, paese, ecc.). */
function joinParts(parts: (string | undefined)[], separator = ', '): string | undefined {
  const filtered = parts.filter((p): p is string => has(p)).map((p) => p.trim());
  return filtered.length > 0 ? filtered.join(separator) : undefined;
}

/** Riga chiave/valore di una lista di dettaglio (`<dt>/<dd>`), saltata se vuota. */
function MetaItem({ label, value }: { label: string; value: string | undefined }) {
  if (!has(value)) return null;
  return (
    <div className="europass-cv__meta-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * Intestazione di sezione con titolo, conteggio opzionale delle voci e, in
 * Editable_Mode, un'azione a destra (il pulsante "Modifica"). L'azione è
 * spinta a fine riga (`margin-left: auto` sul contenitore), così resta
 * allineata a destra del titolo.
 */
function SectionHeading({
  id,
  title,
  count,
  action,
}: {
  id: string;
  title: string;
  count?: number;
  action?: ReactNode;
}) {
  return (
    <div className="europass-cv__section-heading">
      <h2 id={id} className="europass-cv__section-title">
        {title}
      </h2>
      {count !== undefined && count > 0 && (
        <span className="europass-cv__section-count" aria-hidden="true">
          {count}
        </span>
      )}
      {action !== undefined && (
        <div className="europass-cv__section-heading-action">{action}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezioni
// ---------------------------------------------------------------------------

/**
 * Restituisce `true` se i dati personali del CV contengono almeno un campo
 * mostrabile. Esportata per permettere ai consumatori (es. la hero card in cima
 * al dettaglio profilo esterno) di decidere se renderizzare la card senza
 * duplicare i filtri interni.
 */
export function hasPersonalInfoContent(cv: EuropassCv): boolean {
  const info = cv.personalInfo;
  if (info === undefined) return false;
  return (
    has(info.firstName) ||
    has(info.lastName) ||
    has(info.headline) ||
    has(info.email) ||
    has(info.phone) ||
    has(info.dateOfBirth) ||
    has(info.nationality) ||
    has(info.gender) ||
    info.address !== undefined ||
    (info.links ?? []).some(has)
  );
}

/**
 * Card dei dati anagrafici e di contatto (Europass "Personal information").
 *
 * Presentazionale e riusabile: rende i campi valorizzati dei dati personali.
 * Con `hero` assume il trattamento "hero" (bordo sinistro spesso, tinta di
 * sfondo d'accento) usato quando la card è messa in evidenza in cima alla
 * pagina di dettaglio del profilo esterno; senza `hero` è la card neutra usata
 * all'interno del viewer Europass. Se non ci sono campi valorizzati non rende
 * nulla.
 */
export function EuropassPersonalInfoCard({
  cv,
  hero = false,
  ...edit
}: { cv: EuropassCv; hero?: boolean } & EuropassEditableSectionProps) {
  const section = useSingleSectionEditing('personalInfo', edit);
  const info = cv.personalInfo ?? {};
  // In sola lettura, senza contenuto non si rende nulla; in Editable_Mode la
  // card resta sempre visibile (vuota) con il pulsante "Modifica".
  if (!section.active && !hasPersonalInfoContent(cv)) return null;

  const address = info.address;
  const addressText =
    address !== undefined
      ? joinParts([
          address.street,
          joinParts([address.postalCode, address.city], ' '),
          address.region,
          address.country,
        ])
      : undefined;

  const links = (info.links ?? []).filter(has);
  // Nome completo da nome + cognome: mostrato come intestazione della card.
  const fullName = joinParts([info.firstName, info.lastName], ' ');

  return (
    <Card
      className={[
        'europass-cv__card',
        'europass-cv__personal-card',
        hero ? 'europass-cv__personal-card--hero' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {(hero || section.active) && (
        <div className="europass-cv__card-heading">
          {hero && (
            <span className="europass-cv__personal-badge">Dati personali</span>
          )}
          {section.active && (
            <div className="europass-cv__section-heading-action">
              <SectionEditButton label="Modifica" onClick={section.openModal} />
            </div>
          )}
        </div>
      )}
      {fullName !== undefined && (
        <p className="europass-cv__full-name">{fullName}</p>
      )}
      {has(info.headline) && <p className="europass-cv__headline">{info.headline}</p>}
      {section.active &&
        fullName === undefined &&
        !has(info.headline) &&
        !hasPersonalInfoContent(cv) && (
          <p className="europass-cv__placeholder">
            Nessun dato personale. Usa "Modifica" per aggiungerli.
          </p>
        )}
      <dl className="europass-cv__meta">
        <MetaItem label="Email" value={info.email} />
        <MetaItem label="Telefono" value={info.phone} />
        <MetaItem label="Indirizzo" value={addressText} />
        <MetaItem label="Data di nascita" value={info.dateOfBirth} />
        <MetaItem label="Nazionalità" value={info.nationality} />
        <MetaItem label="Genere" value={info.gender} />
      </dl>
      {links.length > 0 && (
        <ul className="europass-cv__links" aria-label="Profili e siti web">
          {links.map((link) => (
            <li key={link}>
              <a href={link} target="_blank" rel="noopener noreferrer">
                {link}
              </a>
            </li>
          ))}
        </ul>
      )}
      {section.active && section.open && (
        <SingleSectionModal
          section="personalInfo"
          current={cv.personalInfo}
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </Card>
  );
}

/** Sintesi/obiettivo professionale (Europass "About me"). */
/** Restituisce `true` se il CV ha un profilo professionale (summary) mostrabile. */
export function hasSummaryContent(cv: EuropassCv): boolean {
  return has(cv.summary);
}

/**
 * Card del profilo professionale (Europass "About me"), esportata per essere
 * mostrata in cima alla pagina di dettaglio del profilo esterno, subito sotto la
 * hero card dei dati personali. Riusa lo stile dei box informativi
 * "Deliverable"/"Compiti" del dettaglio professione: card attenuata (sfondo
 * muted, niente ombra) con titolo piccolo dentro la card. Se il summary è
 * assente non rende nulla.
 */
export function EuropassSummaryCard({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const section = useSingleSectionEditing('summary', edit);
  // In sola lettura, senza summary non si rende nulla; in Editable_Mode la card
  // resta sempre visibile con il pulsante "Modifica".
  if (!section.active && !has(cv.summary)) return null;
  return (
    <Card className="competence-detail__info-card">
      <div className="europass-cv__card-heading">
        <h3 id="europass-summary-title" className="competence-detail__info-title">
          Profilo professionale
        </h3>
        {section.active && (
          <div className="europass-cv__section-heading-action">
            <SectionEditButton label="Modifica" onClick={section.openModal} />
          </div>
        )}
      </div>
      {has(cv.summary) ? (
        <p className="competence-detail__info-text europass-cv__summary">
          {cv.summary}
        </p>
      ) : (
        section.active && (
          <p className="competence-detail__info-text europass-cv__summary europass-cv__placeholder">
            Nessun profilo professionale. Usa "Modifica" per aggiungerlo.
          </p>
        )
      )}
      {section.active && section.open && (
        <SingleSectionModal
          section="summary"
          current={cv.summary}
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </Card>
  );
}

/**
 * Una voce di esperienza lavorativa: riga a due colonne con il periodo a
 * sinistra e, a destra, una card in stile "competenza" (bordo, accento sul
 * bordo sinistro, ombra). Il periodo è fuori dalla card così le date restano
 * Voce di una timeline (esperienza, istruzione o progetti) come accordion
 * nativo: chiusa mostra solo il titolo e un testo secondario sempre visibile
 * (azienda/istituto); aperta rivela i metadati (`detailMeta`), la descrizione e
 * un eventuale contenuto extra (`extra`, es. i chip delle tecnologie). Il
 * periodo è nella colonna a sinistra. Se non c'è dettaglio da rivelare
 * l'accordion resta aperto e la freccia è nascosta.
 */
function TimelineEntry({
  period,
  title,
  secondary,
  detailMeta,
  description,
  extra,
  controls,
}: {
  period: string | undefined;
  title: string;
  secondary: string | undefined;
  detailMeta: string | undefined;
  description: string | undefined;
  extra?: ReactNode;
  /** Controlli dell'Editable_Mode (Modifica/Rimuovi); assenti in read-only. */
  controls?: ReactNode;
}) {
  const hasDetail =
    detailMeta !== undefined || has(description) || extra !== undefined;
  return (
    <li className="europass-cv__timeline-entry">
      <div
        className={[
          'europass-cv__timeline-period',
          period === undefined ? 'europass-cv__timeline-period--empty' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {period !== undefined ? period : 'Data non specificata'}
      </div>
      <details
        className="europass-cv__timeline-card"
        {...(hasDetail ? {} : { open: true })}
      >
        <summary className="europass-cv__timeline-summary">
          <span className="europass-cv__timeline-summary-text">
            <span className="europass-cv__entry-title">{title}</span>
            {secondary !== undefined && (
              <span className="europass-cv__timeline-secondary">{secondary}</span>
            )}
          </span>
          {controls}
          {hasDetail && (
            <span className="europass-cv__timeline-chevron" aria-hidden="true" />
          )}
        </summary>
        {hasDetail && (
          <div className="europass-cv__timeline-detail">
            {detailMeta !== undefined && (
              <p className="europass-cv__entry-subtitle">{detailMeta}</p>
            )}
            {has(description) && (
              <p className="europass-cv__entry-body">{description}</p>
            )}
            {extra}
          </div>
        )}
      </details>
    </li>
  );
}

/** Una voce di esperienza lavorativa. */
function WorkEntry({
  entry,
  controls,
}: {
  entry: EuropassWorkExperience;
  controls?: ReactNode;
}) {
  const place = joinParts([entry.city, entry.country]);
  return (
    <TimelineEntry
      period={formatPeriod(entry.startDate, entry.endDate, entry.current)}
      title={has(entry.jobTitle) ? entry.jobTitle : 'Ruolo non specificato'}
      secondary={has(entry.employer) ? entry.employer : undefined}
      // L'azienda è già nel riepilogo: nel dettaglio restano luogo e settore.
      detailMeta={joinParts([place, entry.sector], ' · ')}
      description={entry.description}
      controls={controls}
    />
  );
}

/** Esperienza lavorativa (Europass "Work experience"). */
export function EuropassWorkExperienceSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  // Mantiene l'indice reale nell'array persistito (il filtro scarta solo le
  // voci completamente vuote): add/edit/remove operano su quell'indice.
  const entries = (cv.workExperience ?? [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => has(entry.jobTitle) || has(entry.employer) || has(entry.description));
  const section = useSectionEditing('workExperience', cv.workExperience ?? [], edit);
  if (entries.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-work-title">
      <SectionHeading
        id="europass-work-title"
        title="Esperienza lavorativa"
        count={entries.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {entries.length > 0 ? (
        <ul className="europass-cv__timeline-list">
          {entries.map(({ entry, index }) => (
            <WorkEntry
              key={`${entry.employer ?? ''}-${entry.startDate ?? ''}-${index}`}
              entry={entry}
              controls={
                section.active ? (
                  <ItemEditControls
                    itemLabel="esperienza lavorativa"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                ) : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <p className="europass-cv__placeholder">
          Nessuna esperienza lavorativa. Usa "Modifica" per aggiungerla.
        </p>
      )}
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="workExperience"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.workExperience ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}

/** Una voce di istruzione/formazione. */
function EducationEntry({
  entry,
  controls,
}: {
  entry: EuropassEducation;
  controls?: ReactNode;
}) {
  const place = joinParts([entry.city, entry.country]);
  return (
    <TimelineEntry
      period={formatPeriod(entry.startDate, entry.endDate, entry.current)}
      title={has(entry.qualification) ? entry.qualification : 'Titolo non specificato'}
      secondary={has(entry.institution) ? entry.institution : undefined}
      // L'istituto è già nel riepilogo: nel dettaglio restano luogo e livello.
      detailMeta={joinParts(
        [place, has(entry.level) ? `Livello ${entry.level}` : undefined],
        ' · ',
      )}
      description={entry.description}
      controls={controls}
    />
  );
}

/** Istruzione e formazione (Europass "Education and training"). */
export function EuropassEducationSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const entries = (cv.education ?? [])
    .map((entry, index) => ({ entry, index }))
    .filter(
      ({ entry }) => has(entry.qualification) || has(entry.institution) || has(entry.description),
    );
  const section = useSectionEditing('education', cv.education ?? [], edit);
  if (entries.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-education-title">
      <SectionHeading
        id="europass-education-title"
        title="Istruzione e formazione"
        count={entries.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {entries.length > 0 ? (
        <ul className="europass-cv__timeline-list">
          {entries.map(({ entry, index }) => (
            <EducationEntry
              key={`${entry.institution ?? ''}-${entry.startDate ?? ''}-${index}`}
              entry={entry}
              controls={
                section.active ? (
                  <ItemEditControls
                    itemLabel="istruzione e formazione"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                ) : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <p className="europass-cv__placeholder">
          Nessun titolo di studio. Usa "Modifica" per aggiungerlo.
        </p>
      )}
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="education"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.education ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}

/** Riga di una lingua: nome + badge madrelingua + abilità/complessivo. */
function LanguageRow({
  lang,
  controls,
}: {
  lang: EuropassLanguageSkill;
  controls?: ReactNode;
}) {
  const skills: { label: string; value: string | undefined }[] = [
    { label: 'Ascolto', value: lang.listening },
    { label: 'Lettura', value: lang.reading },
    { label: 'Interazione orale', value: lang.spokenInteraction },
    { label: 'Produzione orale', value: lang.spokenProduction },
    { label: 'Scrittura', value: lang.writing },
  ].filter((s) => has(s.value));

  return (
    <li className="europass-cv__language">
      <div className="europass-cv__language-header">
        <span className="europass-cv__language-name">
          {has(lang.language) ? lang.language : 'Lingua'}
        </span>
        {lang.mother === true && <Badge tone="info">Madrelingua</Badge>}
        {has(lang.overall) && <Badge tone="neutral">{lang.overall}</Badge>}
        {controls}
      </div>
      {skills.length > 0 && (
        <dl className="europass-cv__language-skills">
          {skills.map((s) => (
            <div key={s.label} className="europass-cv__language-skill">
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

/** Competenze linguistiche (Europass "Language skills"). */
export function EuropassLanguagesSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const langs = (cv.languages ?? [])
    .map((lang, index) => ({ lang, index }))
    .filter(
      ({ lang: l }) =>
        has(l.language) ||
        has(l.overall) ||
        has(l.listening) ||
        has(l.reading) ||
        has(l.spokenInteraction) ||
        has(l.spokenProduction) ||
        has(l.writing),
    );
  const section = useSectionEditing('languages', cv.languages ?? [], edit);
  if (langs.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-languages-title">
      <SectionHeading
        id="europass-languages-title"
        title="Lingue"
        count={langs.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {langs.length > 0 ? (
        <ul className="europass-cv__languages">
          {langs.map(({ lang, index }) => (
            <LanguageRow
              key={`${lang.language ?? ''}-${index}`}
              lang={lang}
              controls={
                section.active ? (
                  <ItemEditControls
                    itemLabel="lingua"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                ) : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <p className="europass-cv__placeholder">
          Nessuna lingua. Usa "Modifica" per aggiungerla.
        </p>
      )}
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="languages"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.languages ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}

/** Voci delle competenze testuali del CV (Europass "Digital/other skills"). */
function europassSkillItems(cv: EuropassCv): { label: string; value: string }[] {
  const skills = cv.skills;
  if (skills === undefined) return [];
  return [
    { label: 'Competenze comunicative', value: skills.communication },
    { label: 'Competenze organizzative e gestionali', value: skills.organisational },
    { label: 'Competenze professionali', value: skills.jobRelated },
    { label: 'Competenze digitali', value: skills.digital },
    { label: 'Altre competenze', value: skills.other },
  ].filter((i): i is { label: string; value: string } => has(i.value));
}

/** `true` se il CV contiene almeno una competenza testuale mostrabile. */
export function hasEuropassSkillsContent(cv: EuropassCv): boolean {
  return europassSkillItems(cv).length > 0;
}

/**
 * Card delle competenze testuali del CV (Europass "Digital/other skills"), resa
 * in stile "box secondario" come la card del Profilo professionale
 * (`competence-detail__info-card`): ogni categoria valorizzata è una coppia
 * titolo/testo. Pensata per stare nella stessa sezione delle competenze del
 * catalogo (DB). Se non ci sono competenze testuali non rende nulla.
 */
export function EuropassSkillsCard({
  cv,
  editOpen = false,
  onEditClose,
  ...edit
}: {
  cv: EuropassCv;
  /**
   * Apertura del modale di modifica CONTROLLATA dall'esterno. La card non ha un
   * proprio pulsante "Modifica": il trigger vive sul titolo della sezione
   * "Competenze" del contenitore (vedi `ExternalCvProfileView`). `onEditClose`
   * è invocata quando il modale va chiuso.
   */
  editOpen?: boolean;
  onEditClose?: () => void;
} & EuropassEditableSectionProps) {
  const section = useSingleSectionEditing('skills', edit);
  const items = europassSkillItems(cv);
  // In sola lettura, senza competenze testuali non si rende nulla; in
  // Editable_Mode la card resta sempre visibile (con il placeholder quando vuota).
  if (items.length === 0 && !section.active) return null;
  return (
    <Card className="competence-detail__info-card europass-cv__skills-card">
      {items.length > 0 ? (
        items.map((item) => (
          <div key={item.label} className="europass-cv__skills-group">
            <h4 className="competence-detail__info-title">{item.label}</h4>
            <p className="competence-detail__info-text">{item.value}</p>
          </div>
        ))
      ) : (
        section.active && (
          <p className="competence-detail__info-text europass-cv__placeholder">
            Nessuna competenza testuale. Usa "Modifica" per aggiungerle.
          </p>
        )
      )}
      {section.active && editOpen && (
        <SingleSectionModal
          section="skills"
          current={cv.skills}
          onSubmit={section.submit}
          onClose={() => onEditClose?.()}
        />
      )}
    </Card>
  );
}

/**
 * Normalizza il nome di una certificazione per confrontare la fonte Credly con
 * quella dichiarata nel CV: minuscolo, spazi collassati e trim. Serve a
 * riconoscere la stessa certificazione anche con piccole differenze di
 * formattazione, così una certificazione presente in entrambe le fonti compare
 * una sola volta (preferendo la card Credly, più ricca).
 */
export function normalizeCertificationName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Un progetto rilevante dichiarato nel CV. */
function ProjectEntry({
  project,
  controls,
}: {
  project: EuropassProject;
  controls?: ReactNode;
}) {
  const technologies = (project.technologies ?? []).filter(has);
  return (
    <TimelineEntry
      period={formatPeriod(project.startDate, project.endDate, undefined)}
      title={has(project.name) ? project.name : 'Progetto'}
      secondary={undefined}
      detailMeta={undefined}
      description={project.description}
      controls={controls}
      extra={
        technologies.length > 0 ? (
          <div className="europass-cv__chips" aria-label="Tecnologie">
            {technologies.map((tech) => (
              <Badge key={tech} tone="neutral">
                {tech}
              </Badge>
            ))}
          </div>
        ) : undefined
      }
    />
  );
}

/** Progetti (sezione "Projects"). */
export function EuropassProjectsSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const projects = (cv.projects ?? [])
    .map((project, index) => ({ project, index }))
    .filter(
      ({ project: p }) => has(p.name) || has(p.description) || (p.technologies ?? []).some(has),
    );
  const section = useSectionEditing('projects', cv.projects ?? [], edit);
  if (projects.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-projects-title">
      <SectionHeading
        id="europass-projects-title"
        title="Progetti"
        count={projects.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {projects.length > 0 ? (
        <ul className="europass-cv__timeline-list">
          {projects.map(({ project, index }) => (
            <ProjectEntry
              key={`${project.name ?? ''}-${index}`}
              project={project}
              controls={
                section.active ? (
                  <ItemEditControls
                    itemLabel="progetto"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                ) : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <p className="europass-cv__placeholder">
          Nessun progetto. Usa "Modifica" per aggiungerlo.
        </p>
      )}
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="projects"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.projects ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}

/** Riconoscimenti e premi (sezione "Honours and awards"). */
export function EuropassAwardsSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const awards = (cv.awards ?? [])
    .map((award, index) => ({ award, index }))
    .filter(({ award: a }) => has(a.title) || has(a.issuer));
  const section = useSectionEditing('awards', cv.awards ?? [], edit);
  if (awards.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-awards-title">
      <SectionHeading
        id="europass-awards-title"
        title="Riconoscimenti"
        count={awards.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {awards.length === 0 && section.active && (
        <p className="europass-cv__placeholder">
          Nessun riconoscimento. Usa "Modifica" per aggiungerlo.
        </p>
      )}
      <ul className="europass-cv__list">
        {awards.map(({ award, index }: { award: EuropassAward; index: number }) => {
          const meta = joinParts([award.issuer, award.date], ' · ');
          return (
            <li key={`${award.title ?? ''}-${index}`} className="europass-cv__entry">
              <div className="europass-cv__entry-header">
                <span className="europass-cv__entry-title">
                  {has(award.title) ? award.title : 'Riconoscimento'}
                </span>
                {section.active && (
                  <ItemEditControls
                    itemLabel="riconoscimento"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                )}
              </div>
              {meta !== undefined && <p className="europass-cv__entry-subtitle">{meta}</p>}
              {has(award.description) && (
                <p className="europass-cv__entry-body">{award.description}</p>
              )}
            </li>
          );
        })}
      </ul>
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="awards"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.awards ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}

/** Pubblicazioni (sezione "Publications"). */
export function EuropassPublicationsSection({
  cv,
  ...edit
}: { cv: EuropassCv } & EuropassEditableSectionProps) {
  const publications = (cv.publications ?? [])
    .map((pub, index) => ({ pub, index }))
    .filter(({ pub: p }) => has(p.title) || has(p.publisher));
  const section = useSectionEditing('publications', cv.publications ?? [], edit);
  if (publications.length === 0 && !section.active) return null;
  return (
    <section className="europass-cv__section" aria-labelledby="europass-publications-title">
      <SectionHeading
        id="europass-publications-title"
        title="Pubblicazioni"
        count={publications.length}
        action={
          section.active ? (
            <SectionEditButton label="Modifica" onClick={section.openAdd} />
          ) : undefined
        }
      />
      {publications.length === 0 && section.active && (
        <p className="europass-cv__placeholder">
          Nessuna pubblicazione. Usa "Modifica" per aggiungerla.
        </p>
      )}
      <ul className="europass-cv__list">
        {publications.map(({ pub, index }: { pub: EuropassPublication; index: number }) => {
          const meta = joinParts([pub.publisher, pub.date], ' · ');
          return (
            <li key={`${pub.title ?? ''}-${index}`} className="europass-cv__entry">
              <div className="europass-cv__entry-header">
                <span className="europass-cv__entry-title">
                  {has(pub.title) ? pub.title : 'Pubblicazione'}
                </span>
                {section.active && (
                  <ItemEditControls
                    itemLabel="pubblicazione"
                    busy={section.busyIndex === index}
                    onEdit={() => section.openEdit(index)}
                    onRemove={() => void section.remove(index)}
                  />
                )}
              </div>
              {meta !== undefined && <p className="europass-cv__entry-subtitle">{meta}</p>}
              {has(pub.reference) && (
                <p className="europass-cv__entry-body europass-cv__entry-body--muted">
                  Rif.: {pub.reference}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {section.active && section.modal !== null && (
        <SectionItemModal
          section="publications"
          mode={section.modal.mode}
          initialItem={
            section.modal.mode === 'edit'
              ? (cv.publications ?? [])[section.modal.index]
              : undefined
          }
          onSubmit={section.submit}
          onClose={section.close}
        />
      )}
    </section>
  );
}


